import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { FaGithub } from 'react-icons/fa';
import { ROUTER_LINKS } from '../common/consts/app-keys.const';
import { SubscriptionPlan } from '../home/subscription/subscription-plan.component';
import useAuth from '../auth/feature/auth';
import packageJson from '../../../package.json';
import { useTheme } from '../common/hooks/use-theme';
import garyStillLight from '../../assets/images/squarebit-subdivs/gary_still_light.jpg';
import garyStillDark from '../../assets/images/squarebit-subdivs/gary_still_dark.jpg';


/**
 * The still the widget resolves into, blurred, holding the frame until it does.
 *
 * The same image and the same treatment the widget's own loading overlay uses,
 * so the handover is one picture coming into focus rather than a cut. It is
 * deliberately the only thing shown before the widget is up: what was here
 * before was a pair of rendered PNGs behind a comparison slider, photographed
 * in a different program at a different framing, which made arriving at the
 * live view a jump between unrelated images.
 *
 * `aria-hidden`, because it carries no information the page does not already
 * state in words: it is the shape of the thing that is coming.
 */
function LoadingStill({ src }: { src: string }): React.ReactElement {
  return (
    <div
      className="relative w-full max-w-[1040px] mx-auto overflow-hidden rounded-xl border border-border-subtle"
      style={{ aspectRatio: ASPECT_RATIO }}
    >
      <img
        src={src}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover blur-xl scale-110"
      />
      <div className="absolute inset-0 bg-surface-base/40" />
    </div>
  );
}

const CAPABILITIES = ['quality', 'nobake', 'performance', 'animation'] as const;

const ASPECT_RATIO = '1919 / 999';

// Loaded lazily so three.js, the subdivision core and the 4MB character stay
// out of the page's initial chunk; the section renders the image slider until
// it's in view.
const SubdivWidget = lazy(() => import('./SubdivWidget'));

const supportsWebGL = (): boolean => {
  try {
    const canvas = document.createElement('canvas');
    return !!(canvas.getContext('webgl2') || canvas.getContext('webgl'));
  } catch {
    return false;
  }
};

/** True once the observed element has scrolled near the viewport (one-shot). */
function useInViewOnce<T extends Element>(): [React.RefObject<T>, boolean] {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setInView(true);
      },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [inView]);
  return [ref, inView];
}

type CompareModel = 'cube' | 'gary';

const SUBDIVS_VERSION =
  (packageJson as { squarebitSubdivs?: { version?: string } }).squarebitSubdivs
    ?.version ?? '';

const SquarebitSubdivsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  // Free tier: send the user to My Products with a claim intent, where they accept the
  // EULA (checkbox) and claim the free plugin. Logged-out visitors sign up first (which
  // records terms acceptance); My Products fulfils the remembered intent once signed in.
  const handleFreeClaim = () => {
    if (!isAuthenticated) {
      try {
        localStorage.setItem('claimSubdivsFree', '1');
      } catch {
        /* best effort — a blocked localStorage just means they re-click after login */
      }
      navigate(ROUTER_LINKS.SIGNUP);
      return;
    }
    navigate(`${ROUTER_LINKS.MANAGEMENT}?getSubdivs=1`);
  };
  // Gary first. The cube explains what subdivision is; the character is what
  // the page is selling, and it is the one worth the visitor's first look.
  const [model, setModel] = useState<CompareModel>('gary');
  const { isDark } = useTheme();
  const [webgl] = useState(supportsWebGL);
  const [compareRef, compareInView] = useInViewOnce<HTMLElement>();

  // The live widget owns its controls in-frame and shows whichever subject the
  // toggle names: the editable cube, or the production character.
  //
  // Everything before it is the SAME still it resolves into, blurred. There
  // used to be a pair of rendered PNGs here behind a comparison slider, and
  // they were a different picture taken in a different program at a different
  // framing, so arriving at the widget meant cutting from one image to an
  // unrelated one and then blurring into a third. One image, held throughout,
  // is what makes the load read as the page settling rather than as three
  // separate things replacing each other.
  const live = webgl;
  const still = isDark ? garyStillDark : garyStillLight;

  return (
    <div className="w-full px-6 md:px-10 py-16 md:py-24">
      {/* Hero */}
      <section>
        {SUBDIVS_VERSION && (
          <Link
            to={ROUTER_LINKS.SQUAREBIT_SUBDIVS_CHANGELOG}
            className="inline-flex items-center gap-[5px] mb-5 px-[9px] py-[2px] rounded-[5px] bg-surface-hover border border-blue-500 text-[10.5px] font-mono font-medium text-blue tracking-wide cursor-pointer hover:bg-surface-hover-section transition-colors"
          >
            <span className="w-[5px] h-[5px] rounded-full bg-blue animate-pulse" />
            {t('management.product.version', { version: SUBDIVS_VERSION })}
          </Link>
        )}
        <h1 aria-label="Squarebit Subdivs" className="hero-lockup text-content-dark">
          {/* Lockup: the wrapper shrink-wraps to the SUBDIVS wordmark. The
              SQUAREBIT eyebrow sits centered at a readable tracking, with
              hairline rules stretching to the wordmark's exact edges — the
              lockup spans the full width without stretching the word itself
              past legibility. */}
          <div className="w-fit">
            <div
              aria-hidden="true"
              className="flex items-center gap-[1em] font-display font-semibold uppercase text-content-muted-alt leading-none text-[clamp(10px,1.7vw,17px)] tracking-[0.42em] mb-[8px] md:mb-[12px]"
            >
              <span className="h-px flex-1 bg-current opacity-30 animate-hero-rule-l" />
              <span className="whitespace-nowrap -mr-[0.42em] animate-hero-eyebrow">
                Squarebit
              </span>
              <span className="h-px flex-1 bg-current opacity-30 animate-hero-rule-r" />
            </div>
            <span className="hero-wordmark block font-display font-extrabold uppercase leading-[0.9] tracking-[-0.045em] animate-hero-wordmark">
              Subdivs
            </span>
            <span className="block font-display font-semibold uppercase text-content-muted-alt mt-[8px] md:mt-[14px] pl-[4px] tracking-[0.34em] text-[clamp(14px,3.2vw,24px)] animate-hero-tagline">
              Realtime
            </span>
          </div>
        </h1>
        <h2 className="font-display font-bold text-[20px] md:text-[26px] tracking-[-0.02em] leading-[1.3] text-content-muted-alt mt-6 max-w-[600px]">
          {t('squarebitSubdivs.title')}
        </h2>
        <p className="text-content-muted-alt text-[16px] md:text-[18px] leading-[1.65] mt-5 max-w-[580px]">
          {t('squarebitSubdivs.subtitle')}
        </p>
        {/* Provenance badge: borrowed authority belongs above the fold.
            Pill -> the official OpenSubdiv site; GitHub via the icon.
            Mobile: the row stays a single flex line (icon shrink-0, so it
            never detaches from the pill); the pill itself is the shrinkable
            item and wraps INTERNALLY — badge text holds one line, the long
            suffix drops to its own line(s) inside the pill. The GitHub icon
            grows to a 34px touch target below sm; desktop (sm+) renders
            exactly as before. */}
        <div className="flex sm:inline-flex items-center gap-[8px] mt-5">
          <a
            href="https://opensubdiv.org/docs/intro.html"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex min-w-0 flex-wrap items-center gap-x-[7px] gap-y-[2px] px-[11px] py-[4px] rounded-[6px] bg-surface-hover border border-blue-500 font-mono text-[11px] font-medium text-blue tracking-wide hover:bg-surface-hover-section transition-colors"
          >
            <span className="w-[5px] h-[5px] rounded-full bg-blue shrink-0" />
            <span className="whitespace-nowrap">
              {t('squarebitSubdivs.openSubdivBadge')}
            </span>
            <span className="text-content-muted-alt normal-case min-w-0">
              · {t('squarebitSubdivs.openSubdivBadgeSuffix')}
            </span>
          </a>
          <a
            href="https://github.com/PixarAnimationStudios/OpenSubdiv"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="OpenSubdiv on GitHub"
            title="OpenSubdiv on GitHub"
            className="inline-flex items-center justify-center shrink-0 w-[34px] h-[34px] sm:w-[26px] sm:h-[26px] rounded-[6px] border border-border-brand text-content-muted-alt hover:text-content-dark hover:bg-surface-hover transition-colors"
          >
            <FaGithub size={14} />
          </a>
        </div>
        <div className="flex items-center gap-x-6 gap-y-3 mt-8 flex-wrap">
          <button
            type="button"
            onClick={() =>
              document
                .getElementById('pricing')
                ?.scrollIntoView({ behavior: 'smooth' })
            }
            className="inline-flex items-center rounded-full bg-blue text-surface-base font-body font-semibold text-[15px] px-[26px] py-[13px] transition-all duration-200 hover:bg-blue-deep-1 hover:shadow-[0px_0px_0px_4px_rgba(93,225,255,0.25)]"
          >
            {t('squarebitSubdivs.heroCta')}
          </button>
          <Link
            to={ROUTER_LINKS.SQUAREBIT_SUBDIVS_DOCS}
            className="font-body text-[14px] font-medium text-blue hover:text-blue-deep-1 transition-colors"
          >
            {t('common.readTheDocs')} →
          </Link>
        </div>
      </section>

      {/* Before / after comparison */}
      <section ref={compareRef} className="mt-14 md:mt-20">
        {/* Model toggle */}
        <div className="flex justify-center mb-6">
          <div
            role="tablist"
            aria-label={t('squarebitSubdivs.compareModel')}
            className="inline-flex items-center gap-1 rounded-full border border-border-brand bg-surface-base p-1"
          >
            {(['cube', 'gary'] as const).map((m) => {
              const selected = model === m;
              return (
                <button
                  key={m}
                  role="tab"
                  type="button"
                  aria-selected={selected}
                  onClick={() => setModel(m)}
                  className={`rounded-full px-[20px] py-[8px] font-body text-[14px] font-semibold transition-all duration-200 ${
                    selected
                      ? 'bg-blue text-surface-base shadow-[0px_0px_0px_3px_rgba(93,225,255,0.2)]'
                      : 'text-content-muted-alt hover:text-content-dark'
                  }`}
                >
                  {t(
                    `squarebitSubdivs.compareToggle${m === 'cube' ? 'Cube' : 'Gary'}`
                  )}
                </button>
              );
            })}
          </div>
        </div>
        {live && compareInView ? (
          <Suspense fallback={<LoadingStill src={still} />}>
            <SubdivWidget aspectRatio={ASPECT_RATIO} subject={model} />
          </Suspense>
        ) : (
          <LoadingStill src={still} />
        )}


        {live && (
          <p className="text-content-muted-alt text-[13px] md:text-[14px] italic text-center mt-4">
            {t('squarebitSubdivs.liveCaption')}
          </p>
        )}
      </section>

      {/* What it does */}
      <section className="mt-20 md:mt-28">
        <h2 className="font-display font-bold text-[26px] md:text-[34px] tracking-[-0.02em] text-content-dark mb-3">
          {t('squarebitSubdivs.whatTitle')}
        </h2>
        <p className="text-content-muted-alt text-[15px] md:text-[16px] leading-[1.6] max-w-[620px] mb-10">
          {t('squarebitSubdivs.whatSubtitle')}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-[16px] md:gap-[20px]">
          {CAPABILITIES.map((c) => (
            <div
              key={c}
              className="bg-surface-base border border-border-brand rounded-[16px] p-[24px] md:p-[28px] shadow-[0_2px_12px_rgba(34,211,238,0.06)]"
            >
              <h3 className="font-display font-bold text-[18px] md:text-[20px] text-content-dark mb-[10px]">
                {t(`squarebitSubdivs.cap_${c}_title`)}
              </h3>
              <p className="text-content-secondary text-[14px] md:text-[15px] leading-[1.7]">
                {t(`squarebitSubdivs.cap_${c}_body`)}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Before / after */}
      <section className="mt-20 md:mt-28 rounded-[20px] bg-[var(--subscription-brand-100)] border border-border-brand px-[24px] md:px-[48px] py-[36px] md:py-[48px]">
        <h2 className="font-display font-bold text-[26px] md:text-[34px] tracking-[-0.02em] text-content-dark mb-3">
          {t('squarebitSubdivs.howTitle')}
        </h2>
        <p className="text-content-secondary text-[15px] md:text-[16px] leading-[1.7] max-w-[720px] mb-8">
          {t('squarebitSubdivs.howIntro')}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-[20px] md:gap-[28px]">
          <div>
            <h3 className="font-display font-bold text-[17px] md:text-[19px] text-content-muted-alt mb-[8px]">
              {t('squarebitSubdivs.beforeTitle')}
            </h3>
            <p className="text-content-secondary text-[14px] md:text-[15px] leading-[1.7]">
              {t('squarebitSubdivs.beforeBody')}
            </p>
          </div>
          <div>
            <h3 className="font-display font-bold text-[17px] md:text-[19px] text-blue mb-[8px]">
              {t('squarebitSubdivs.afterTitle')}
            </h3>
            <p className="text-content-secondary text-[14px] md:text-[15px] leading-[1.7]">
              {t('squarebitSubdivs.afterBody')}
            </p>
          </div>
        </div>
        <p className="text-content-muted-alt text-[13px] md:text-[14px] leading-[1.7] mt-8 pt-6 border-t border-border-brand">
          <Trans
            i18nKey="squarebitSubdivs.openSubdivCredit"
            components={[
              <a
                href="https://github.com/PixarAnimationStudios/OpenSubdiv"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue hover:text-blue-deep-1 underline"
              >
                OpenSubdiv
              </a>
            ]}
          />
        </p>
      </section>

      {/* Pricing */}
      <section id="pricing" className="mt-20 md:mt-28 scroll-mt-24">
        <h2 className="font-display font-extrabold text-[28px] md:text-[40px] tracking-[-0.02em] text-content-dark text-center mb-3">
          {t('squarebitSubdivs.pricingTitle')}
        </h2>
        <p className="text-content-muted-alt text-[15px] md:text-[16px] leading-[1.6] text-center max-w-[560px] mx-auto mb-10">
          {t('squarebitSubdivs.pricingSubtitle')}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-[14px] max-w-[1020px] mx-auto items-stretch">
          {/* Free: claim-on-click. Records a keyless entitlement (so the download
                appears in My Products only for opt-in users) and routes there;
                logged-out visitors sign up first. */}
          <SubscriptionPlan
            caption={t('squarebitSubdivs.freeCaption')}
            customPrice={t('squarebitSubdivs.freePrice')}
            duration={t('squarebitSubdivs.freeDuration')}
            plan="free"
            product="squarebit-subdivs"
            features={
              t('squarebitSubdivs.freeFeatures', {
                returnObjects: true
              }) as string[]
            }
            finePrint={t('squarebitSubdivs.freeFinePrint')}
            buttonText={t('squarebitSubdivs.freeButton')}
            onCta={handleFreeClaim}
          />
          {/* Perpetual: $99 one-time, per seat (mode:'payment'). The main self-serve tier. */}
          <SubscriptionPlan
            caption={t('squarebitSubdivs.perpetualCaption')}
            customPrice={t('squarebitSubdivs.perpetualPrice')}
            duration={t('squarebitSubdivs.perpetualDuration')}
            plan="perpetual"
            product="squarebit-subdivs"
            badgeText={t('squarebitSubdivs.perpetualBadge')}
            features={
              t('squarebitSubdivs.perpetualFeatures', {
                returnObjects: true
              }) as string[]
            }
            finePrint={t('squarebitSubdivs.perpetualFinePrint')}
            buttonText={t('squarebitSubdivs.perpetualButton')}
            isPopular
          />
          {/* Studio: custom pricing, contact sales — no self-serve checkout.
                plan is required by the card props but unused in contact mode. */}
          <SubscriptionPlan
            caption={t('squarebitSubdivs.studioCaption')}
            customPrice={t('squarebitSubdivs.studioPrice')}
            duration={t('squarebitSubdivs.studioDuration')}
            plan="perpetual"
            product="squarebit-subdivs"
            features={
              t('squarebitSubdivs.studioFeatures', {
                returnObjects: true
              }) as string[]
            }
            finePrint={t('squarebitSubdivs.studioFinePrint')}
            buttonText={t('squarebitSubdivs.studioButton')}
            contactHref={ROUTER_LINKS.CONTACT}
          />
        </div>
      </section>

      {/* Final CTA */}
      <section className="mt-20 md:mt-28 mb-4 text-center">
        <h2 className="font-display font-extrabold text-[28px] md:text-[40px] tracking-[-0.02em] text-content-dark mb-4">
          {t('squarebitSubdivs.ctaTitle')}
        </h2>
        <p className="text-content-muted-alt text-[15px] md:text-[17px] leading-[1.6] max-w-[560px] mx-auto mb-8">
          {t('squarebitSubdivs.ctaBody')}
        </p>
        <Link
          to={ROUTER_LINKS.CONTACT}
          className="inline-flex items-center rounded-full bg-blue text-surface-base font-body font-semibold text-[16px] px-[30px] py-[14px] transition-all duration-200 hover:bg-blue-deep-1 hover:shadow-[0px_0px_0px_4px_rgba(93,225,255,0.25)]"
        >
          {t('squarebitSubdivs.ctaButton')}
        </Link>
      </section>
    </div>
  );
};

export default SquarebitSubdivsPage;
